import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { tenantAPI, teamAPI, servicesAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Building2, Users, Stethoscope, Save, Plus, Trash2,
} from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user, tenant, loadUser } = useAuth();
  const [tenantForm, setTenantForm] = useState({ name: "", phone: "", email: "", address: "" });
  const [team, setTeam] = useState([]);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showMember, setShowMember] = useState(false);
  const [showService, setShowService] = useState(false);
  const [memberForm, setMemberForm] = useState({ name: "", email: "", password: "", role: "physio", phone: "" });
  const [serviceForm, setServiceForm] = useState({ name: "", description: "", duration_minutes: 60, price: 0 });
  const [modulesForm, setModulesForm] = useState({ clinical: true, strength: false });
  const [savingModules, setSavingModules] = useState(false);

  useEffect(() => {
    if (tenant) {
      setTenantForm({ name: tenant.name || "", phone: tenant.phone || "", email: tenant.email || "", address: tenant.address || "" });
      setModulesForm({ clinical: tenant.modules?.clinical !== false, strength: !!tenant.modules?.strength });
    }
    teamAPI.list().then((r) => setTeam(r.data.members || [])).catch(() => {});
    servicesAPI.list().then((r) => setServices(r.data.services || [])).catch(() => {});
  }, [tenant]);

  const saveTenant = async () => {
    setSaving(true);
    try {
      await tenantAPI.update(tenantForm);
      toast.success("Informacion actualizada");
      loadUser();
    } catch {
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  const saveModules = async () => {
    setSavingModules(true);
    try {
      await tenantAPI.update({ modules: { clinical: true, strength: modulesForm.strength } });
      toast.success("Modulos actualizados");
      loadUser();
    } catch {
      toast.error("Error al actualizar modulos");
    } finally {
      setSavingModules(false);
    }
  };

  const addMember = async (e) => {
    e.preventDefault();
    try {
      await teamAPI.add(memberForm);
      toast.success("Miembro agregado");
      setShowMember(false);
      setMemberForm({ name: "", email: "", password: "", role: "physio", phone: "" });
      const r = await teamAPI.list();
      setTeam(r.data.members || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const addService = async (e) => {
    e.preventDefault();
    try {
      await servicesAPI.create({ ...serviceForm, price: parseFloat(serviceForm.price) || 0, duration_minutes: parseInt(serviceForm.duration_minutes) || 60 });
      toast.success("Servicio creado");
      setShowService(false);
      setServiceForm({ name: "", description: "", duration_minutes: 60, price: 0 });
      const r = await servicesAPI.list();
      setServices(r.data.services || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const deleteService = async (id) => {
    try {
      await servicesAPI.delete(id);
      toast.success("Servicio eliminado");
      setServices(services.filter((s) => s.id !== id));
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const ROLE_LABELS = { owner: "Propietario", physio: "Fisioterapeuta", coach: "Coach S&C", assistant: "Asistente", admin: "Administrador" };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Configuracion</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Administra tu clinica, equipo y servicios</p>
      </div>

      <Tabs defaultValue="clinic" className="w-full">
        <TabsList>
          <TabsTrigger value="clinic" data-testid="tab-clinic"><Building2 className="w-3.5 h-3.5 mr-1.5" />Clinica</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team"><Users className="w-3.5 h-3.5 mr-1.5" />Equipo</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services"><Stethoscope className="w-3.5 h-3.5 mr-1.5" />Servicios</TabsTrigger>
        </TabsList>

        <TabsContent value="clinic" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Datos de la clinica</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Nombre de la clinica</Label>
                  <Input value={tenantForm.name} onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })} data-testid="clinic-name-input" />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input value={tenantForm.phone} onChange={(e) => setTenantForm({ ...tenantForm, phone: e.target.value })} data-testid="clinic-phone-input" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={tenantForm.email} onChange={(e) => setTenantForm({ ...tenantForm, email: e.target.value })} data-testid="clinic-email-input" />
                </div>
                <div>
                  <Label>Direccion</Label>
                  <Input value={tenantForm.address} onChange={(e) => setTenantForm({ ...tenantForm, address: e.target.value })} data-testid="clinic-address-input" />
                </div>
              </div>
              <Button onClick={saveTenant} disabled={saving} className="rounded-full" data-testid="save-clinic-btn">
                <Save className="w-4 h-4 mr-1.5" />{saving ? "Guardando..." : "Guardar cambios"}
              </Button>
            </CardContent>
          </Card>

          {user?.role === "owner" && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Modulos</CardTitle>
                <p className="text-xs text-muted-foreground">Activa el modulo de Fuerza y Acondicionamiento para atender atletas.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">Clinico</p>
                    <p className="text-xs text-muted-foreground">Expedientes, evaluaciones, episodios SOAP</p>
                  </div>
                  <Switch checked disabled />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">Fuerza y Acondicionamiento (S&amp;C)</p>
                    <p className="text-xs text-muted-foreground">Atletas y programas de entrenamiento</p>
                  </div>
                  <Switch checked={modulesForm.strength} onCheckedChange={(v) => setModulesForm({ ...modulesForm, strength: v })} data-testid="module-strength-switch" />
                </div>
                <Button onClick={saveModules} disabled={savingModules} className="rounded-full" data-testid="save-modules-btn">
                  <Save className="w-4 h-4 mr-1.5" />{savingModules ? "Guardando..." : "Guardar modulos"}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="team" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button size="sm" className="rounded-full" onClick={() => setShowMember(true)} data-testid="add-member-btn">
              <Plus className="w-4 h-4 mr-1.5" />Agregar miembro
            </Button>
          </div>
          <div className="space-y-2">
            {team.map((m) => (
              <Card key={m.id} data-testid={`team-member-${m.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                    {m.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{m.name}</p>
                    <p className="text-xs text-muted-foreground">{m.email}</p>
                  </div>
                  <Badge variant="secondary" className="text-xs">{ROLE_LABELS[m.role] || m.role}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="services" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button size="sm" className="rounded-full" onClick={() => setShowService(true)} data-testid="add-service-btn">
              <Plus className="w-4 h-4 mr-1.5" />Agregar servicio
            </Button>
          </div>
          {services.length > 0 ? (
            <div className="space-y-2">
              {services.map((s) => (
                <Card key={s.id} data-testid={`service-card-${s.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.duration_minutes} min &middot; ${s.price?.toLocaleString("es-MX")}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteService(s.id)} data-testid={`delete-service-${s.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-muted-foreground">
              <Stethoscope className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Sin servicios configurados</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Member Dialog */}
      <Dialog open={showMember} onOpenChange={setShowMember}>
        <DialogContent className="sm:max-w-md" data-testid="add-member-dialog">
          <DialogHeader><DialogTitle>Agregar miembro al equipo</DialogTitle></DialogHeader>
          <form onSubmit={addMember} className="space-y-4">
            <div><Label>Nombre</Label><Input value={memberForm.name} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} required data-testid="member-name" /></div>
            <div><Label>Email</Label><Input type="email" value={memberForm.email} onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} required data-testid="member-email" /></div>
            <div><Label>Contrasena temporal</Label><Input value={memberForm.password} onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })} placeholder="Minimo 6 caracteres" data-testid="member-password" /></div>
            <div>
              <Label>Rol</Label>
              <Select value={memberForm.role} onValueChange={(v) => setMemberForm({ ...memberForm, role: v })}>
                <SelectTrigger data-testid="member-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="physio">Fisioterapeuta</SelectItem>
                  <SelectItem value="coach">Coach S&amp;C</SelectItem>
                  <SelectItem value="assistant">Asistente</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowMember(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" data-testid="member-save-btn">Agregar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Service Dialog */}
      <Dialog open={showService} onOpenChange={setShowService}>
        <DialogContent className="sm:max-w-md" data-testid="add-service-dialog">
          <DialogHeader><DialogTitle>Agregar servicio</DialogTitle></DialogHeader>
          <form onSubmit={addService} className="space-y-4">
            <div><Label>Nombre del servicio</Label><Input value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} required data-testid="service-name" /></div>
            <div><Label>Descripcion</Label><Input value={serviceForm.description} onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })} data-testid="service-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Duracion (min)</Label><Input type="number" value={serviceForm.duration_minutes} onChange={(e) => setServiceForm({ ...serviceForm, duration_minutes: e.target.value })} data-testid="service-duration" /></div>
              <div><Label>Precio ($)</Label><Input type="number" step="0.01" value={serviceForm.price} onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })} data-testid="service-price" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowService(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" data-testid="service-save-btn">Crear servicio</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
